import {
  collection, doc, getDoc, getDocs, query, where, orderBy, limit,
  addDoc, updateDoc, onSnapshot, serverTimestamp
} from 'firebase/firestore';
import { db, COLLECTIONS } from './config';

// Get or create a conversation between two users
export const getOrCreateConversation = async (userId1, userId2, user1Name, user2Name) => {
  try {
    // Check if conversation exists
    const q1 = query(
      collection(db, COLLECTIONS.CONVERSATIONS),
      where('participants', 'array-contains', userId1)
    );

    const snapshot = await getDocs(q1);
    const existing = snapshot.docs.find(d => {
      const data = d.data();
      return data.participants.includes(userId2);
    });

    if (existing) {
      return { id: existing.id, ...existing.data() };
    }

    // Create new conversation
    const convRef = await addDoc(collection(db, COLLECTIONS.CONVERSATIONS), {
      participants: [userId1, userId2],
      participantNames: { [userId1]: user1Name, [userId2]: user2Name },
      lastMessage: null,
      lastMessageAt: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    return { id: convRef.id, participants: [userId1, userId2] };
  } catch (error) {
    console.error('Error getting/creating conversation:', error);
    throw error;
  }
};

// Send a message
export const sendMessage = async (conversationId, senderId, senderName, text) => {
  try {
    const msgRef = await addDoc(
      collection(db, COLLECTIONS.CONVERSATIONS, conversationId, 'messages'),
      {
        senderId,
        senderName,
        text,
        read: false,
        createdAt: serverTimestamp()
      }
    );

    // Update conversation's last message
    await updateDoc(doc(db, COLLECTIONS.CONVERSATIONS, conversationId), {
      lastMessage: text,
      lastMessageSender: senderId,
      lastMessageAt: serverTimestamp()
    });

    return { id: msgRef.id };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Get user's conversations
export const getUserConversations = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CONVERSATIONS),
      where('participants', 'array-contains', userId)
    );

    const snapshot = await getDocs(q);
    const conversations = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      lastMessageAt: d.data().lastMessageAt?.toDate?.() || d.data().lastMessageAt
    }));

    return conversations.sort((a, b) => {
      const dateA = a.lastMessageAt || new Date(0);
      const dateB = b.lastMessageAt || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }
};

// Subscribe to messages in a conversation (real-time)
export const subscribeToMessages = (conversationId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.CONVERSATIONS, conversationId, 'messages'),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt
    }));
    callback(messages);
  });
};

// Mark messages as read
export const markMessagesAsRead = async (conversationId, userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CONVERSATIONS, conversationId, 'messages'),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    const updates = snapshot.docs
      .filter(d => d.data().senderId !== userId)
      .map(d => updateDoc(
        doc(db, COLLECTIONS.CONVERSATIONS, conversationId, 'messages', d.id),
        { read: true }
      ));

    await Promise.all(updates);
  } catch (error) {
    console.error('Error marking messages read:', error);
  }
};

// Get unread message count
export const getUnreadCount = async (userId) => {
  try {
    const convQuery = query(
      collection(db, COLLECTIONS.CONVERSATIONS),
      where('participants', 'array-contains', userId)
    );
    const convSnap = await getDocs(convQuery);

    let totalUnread = 0;
    for (const convDoc of convSnap.docs) {
      const msgQuery = query(
        collection(db, COLLECTIONS.CONVERSATIONS, convDoc.id, 'messages'),
        where('read', '==', false)
      );
      const msgSnap = await getDocs(msgQuery);
      totalUnread += msgSnap.docs.filter(d => d.data().senderId !== userId).length;
    }

    return totalUnread;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};
